import { cva, type VariantProps } from 'class-variance-authority';
import { useMemo, type ReactNode } from 'react';

import { cn, Flex, Spinner, Text } from '@kognitos/lattice';
import { useTranslation } from 'react-i18next';

import {
  ScreencastViewer,
  type ScreencastState,
} from '@/shared/components/ScreencastViewer';
import { resolveScreencastUrl } from '@/shared/utils/browser-urls';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';

import { EBrowserPlayerState, EBrowserPreviewFabState } from '../types';
import { BrowserChrome } from './BrowserChrome';
import {
  BrowserPreviewFob,
  type IBrowserPreviewFabProps,
} from './BrowserPreviewFob';
import { BrowserVideoView } from './BrowserVideoView';

const browserPlayerVariants = cva(
  'relative w-full rounded-lg overflow-hidden',
  {
    variants: {
      state: {
        [EBrowserPlayerState.LOADING]: 'flex items-center justify-center',
        [EBrowserPlayerState.LIVE]: 'flex items-start',
        [EBrowserPlayerState.REPLAY]: 'flex items-start',
      },
    },
    defaultVariants: {
      state: EBrowserPlayerState.LOADING,
    },
  },
);

/**
 * Props for the BrowserPlayer component.
 */
export interface IBrowserPlayerProps
  extends VariantProps<typeof browserPlayerVariants>,
    Pick<
      IBrowserPreviewFabProps,
      | 'onPlayPause'
      | 'onSkipBack'
      | 'onSkipForward'
      | 'onMaximize'
      | 'stepMarkers'
      | 'onStepMarkerClick'
      | 'disabled'
      | 'connected'
      | 'showLiveStatus'
    > {
  /** The current state of the preview. */
  state?: EBrowserPlayerState;
  /** The state of the FAB controls. */
  fabState?: EBrowserPreviewFabState;
  /** Whether replay playback is running, for the FAB's play/pause icon. */
  isPlaying?: boolean;
  /** Current timestamp for replay mode. */
  timestamp?: string;
  /** Total recording length, shown at the right of the controls. */
  duration?: string;
  /** Playback position as a 0-100 percentage of the recording's duration. */
  progress?: number;
  /** Custom content to render inside the browser frame. */
  children?: ReactNode;
  /** Height of the preview. If not provided, uses 16:9 aspect ratio. */
  height?: string | number;
  /** Additional CSS classes. */
  className?: string;
  /** Loading message to display. */
  loadingMessage?: string;
  /** Live view URL for the browser session (internal K8s URL with /display/{pod} path). */
  liveViewUrl?: string;
  /**
   * Playable blob URL for replay mode. Required when `state === REPLAY`;
   * produced by {@link useReplayVideo}. Ignored otherwise.
   */
  videoUrl?: string | null;
  /** When true, disables keyboard/mouse input (view only mode). */
  readOnly?: boolean;
  /** When true, shows browser chrome UI (title bar, address bar). Use for expanded/preview mode. */
  showChrome?: boolean;
  /** Callback fired when the screencast connection state changes. */
  onScreencastStateChange?: (state: ScreencastState) => void;
  /** When false, just the screen in a fitted 16:9 frame, with no controls. Defaults to true. */
  showControls?: boolean;
  /** Omit the player frame when its host already provides a card. */
  frameless?: boolean;
  /** Ref forwarded to the replay video for step seeking. */
  videoRef?: React.MutableRefObject<HTMLVideoElement | null>;
  /** Ref forwarded to the screen area, the fullscreen target while live. */
  screenRef?: React.Ref<HTMLDivElement>;
  /** Reports replay progress in seconds. */
  onVideoTimeUpdate?: (currentTime: number) => void;
  /** Reports the recording's total length in seconds, once known. */
  onVideoDurationChange?: (duration: number) => void;
  /** Reports replay play/pause state. */
  onVideoPlayStateChange?: (isPlaying: boolean) => void;
  /** Reports that the replay video has decoded its first frame. */
  onVideoReady?: () => void;
  /** Reports that the replay video failed to load or decode. */
  onVideoError?: () => void;
  /**
   * Covers the frame with a loader while the replay is being prepared, so the
   * ended screencast and the not-yet-decoded video never flash on screen.
   */
  replayLoading?: boolean;
}

interface IBrowserPlayerLoaderProps {
  message: string;
  className?: string;
  'data-testid'?: string;
}

/**
 * Spinner plus a short message, shared by every BrowserPlayer loading state
 * so they stay visually identical as one hands off to the next.
 *
 * @param props - Loader props
 * @returns Centered loader
 */
function BrowserPlayerLoader({
  message,
  className,
  'data-testid': testId,
}: IBrowserPlayerLoaderProps) {
  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      gap={2}
      className={className}
      data-testid={testId}
    >
      <Spinner size="sm" />
      <Text level="small" color="muted">
        {message}
      </Text>
    </Flex>
  );
}

/**
 * BrowserPlayer displays a browser preview with various states.
 *
 * Uses CDP screencast (SSE) for live browser display.
 *
 * @param props - Component props
 * @returns BrowserPlayer component
 *
 * @example
 * // Loading state
 * <BrowserPlayer
 *   state={EBrowserPlayerState.LOADING}
 *   loadingMessage="Initializing browser..."
 * />
 *
 * @example
 * // Live state with active screencast
 * <BrowserPlayer
 *   state={EBrowserPlayerState.LIVE}
 *   liveViewUrl="ws://cluster.local/display/browser-pod-123"
 * />
 *
 * @example
 * // Replay state with recorded session video
 * <BrowserPlayer
 *   state={EBrowserPlayerState.REPLAY}
 *   liveViewUrl="/api/v1/browser/namespaces/org-acme-ws-prod/sessions/abc/screencast"
 * />
 */
export function BrowserPlayer({
  state = EBrowserPlayerState.LOADING,
  fabState = EBrowserPreviewFabState.PLAYING,
  isPlaying,
  timestamp,
  duration,
  progress,
  stepMarkers,
  onStepMarkerClick,
  disabled,
  connected,
  showLiveStatus,
  children,
  height,
  className,
  loadingMessage = 'Initializing browser...',
  liveViewUrl,
  videoUrl,
  readOnly = false,
  showChrome = false,
  showControls = true,
  frameless = false,
  onScreencastStateChange,
  onPlayPause,
  onSkipBack,
  onSkipForward,
  onMaximize,
  videoRef,
  screenRef,
  onVideoTimeUpdate,
  onVideoDurationChange,
  onVideoPlayStateChange,
  onVideoReady,
  onVideoError,
  replayLoading = false,
}: IBrowserPlayerProps) {
  'use no memo';
  const { t } = useTranslation('browser-automation');
  const orgId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const isLoading = state === EBrowserPlayerState.LOADING;
  const isLive = state === EBrowserPlayerState.LIVE;
  const isReplay = state === EBrowserPlayerState.REPLAY;

  // Only apply height style if explicitly provided (otherwise use aspect-video)
  const heightStyle = height
    ? typeof height === 'number'
      ? `${height}px`
      : height
    : undefined;

  const screencastUrl = useMemo(() => {
    if (!liveViewUrl || !orgId || !workspaceId) return undefined;
    try {
      return resolveScreencastUrl(liveViewUrl, orgId, workspaceId) ?? undefined;
    } catch {
      return undefined;
    }
  }, [liveViewUrl, orgId, workspaceId]);

  return (
    <div
      className={cn(
        browserPlayerVariants({ state }),
        !frameless && 'border border-border bg-card p-4 shadow-md',
        !height && !showChrome && !showControls && 'aspect-video',
        showControls && 'flex-col',
        showChrome && 'p-0',
        className,
      )}
      style={heightStyle ? { height: heightStyle } : undefined}
    >
      <div
        ref={screenRef}
        className={cn(
          'relative',
          showControls && 'w-full min-h-0',
          showControls && !height && !showChrome && 'aspect-video',
          showControls && height && 'flex-1',
          !showControls && 'h-full w-full',
        )}
      >
        {/* Loading State Content */}
        {isLoading && (
          <BrowserPlayerLoader
            message={loadingMessage}
            className="h-full w-full"
            data-testid="browser-player-loading"
          />
        )}

        {/* Browser Frame Content */}
        {isLive &&
          screencastUrl &&
          (showChrome ? (
            <BrowserChrome className="flex-1 h-full min-h-px min-w-px">
              <ScreencastViewer
                screencastUrl={screencastUrl}
                readOnly={readOnly}
                onStateChange={onScreencastStateChange}
                fit={!showControls}
              />
              {children}
            </BrowserChrome>
          ) : (
            <Flex
              direction="column"
              flex="1"
              height="full"
              overflow="hidden"
              className="min-h-px min-w-px rounded-md"
            >
              <ScreencastViewer
                screencastUrl={screencastUrl}
                readOnly={readOnly}
                onStateChange={onScreencastStateChange}
                fit={!showControls}
              />
              {children}
            </Flex>
          ))}

        {/* Replay state */}
        {isReplay &&
          videoUrl &&
          (showChrome ? (
            <BrowserChrome className="flex-1 h-full min-h-px min-w-px">
              <BrowserVideoView
                videoUrl={videoUrl}
                videoRef={videoRef}
                onTimeUpdate={onVideoTimeUpdate}
                onDurationChange={onVideoDurationChange}
                onPlayStateChange={onVideoPlayStateChange}
                onReady={onVideoReady}
                onError={onVideoError}
                // Two control bars stacked on one video is worse than either.
                hideNativeControls
              />
            </BrowserChrome>
          ) : (
            <Flex
              direction="column"
              flex="1"
              height="full"
              overflow="hidden"
              className="min-h-px min-w-px rounded-md"
            >
              <BrowserVideoView
                videoUrl={videoUrl}
                videoRef={videoRef}
                onTimeUpdate={onVideoTimeUpdate}
                onDurationChange={onVideoDurationChange}
                onPlayStateChange={onVideoPlayStateChange}
                onReady={onVideoReady}
                onError={onVideoError}
                // Two control bars stacked on one video is worse than either.
                hideNativeControls
              />
            </Flex>
          ))}

        {replayLoading && (
          <BrowserPlayerLoader
            message={t('browserAutomation.video.loading')}
            className="absolute inset-0 z-10 rounded-md bg-background"
            data-testid="browser-player-replay-loading"
          />
        )}
      </div>

      {showControls && (
        <div
          className="w-full shrink-0 border-t border-border bg-card px-3 py-2"
          onClick={event => event.stopPropagation()}
          data-testid="browser-player-controls"
        >
          <BrowserPreviewFob
            state={fabState}
            isPlaying={isPlaying}
            timestamp={timestamp}
            duration={duration}
            progress={progress}
            stepMarkers={stepMarkers}
            onStepMarkerClick={onStepMarkerClick}
            disabled={disabled}
            connected={connected}
            showLiveStatus={showLiveStatus}
            onPlayPause={onPlayPause}
            onSkipBack={onSkipBack}
            onSkipForward={onSkipForward}
            onMaximize={onMaximize}
          />
        </div>
      )}
    </div>
  );
}

export { browserPlayerVariants };
